import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as StarredIcon } from '../../../../assets/images/starred.svg';
import CustomMultiSelect from '../../../../components/shared/CustomMultiSelect/CustomMultiSelect';
import CustomField from '../../../../components/shared/CustomTextField/CustomTextField';
import FiltersDrawer from '../../../../components/shared/FiltersDrawer/FiltersDrawer';
import CustomSwitch from '../../../../components/shared/Switch/Switch';
import { CONTACT_SOURCES } from '../../../../constants/commonConstants';
import { resetContactFilters, setContactFilters } from '../../../../redux/actions/filtersActions';
import { getTags } from '../../../../redux/actions/networkActions';

import styles from './ContactFilters.module.scss';

const ContactFilters = ({ toggleFilters, filtersDrawer, getFilteredContacts }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filtersReducer.contactFilters);
  const tags = useSelector((state) => state.networkReducer.tags);
  const [filtersData, setFiltersData] = useState(filters);

  const translatedContactSources = useMemo(
    () => CONTACT_SOURCES.map((source) => ({ ...source, label: t(source.label) })),
    [t]
  );

  const newFiltersCount = useMemo(() => {
    let count = 0;
    if (filtersData.referrals_sent) count += 1;
    if (filtersData.tags.length) count += 1;
    if (filtersData.sources.length) count += 1;
    if (filtersData.starred) count += 1;
    return count;
  }, [filtersData]);

  const formatTags = useMemo(() => tags.map((tag) => ({ id: tag.id, value: tag.name, label: tag.name })), [tags]);

  const updateFilters = useCallback(() => {
    dispatch(setContactFilters(filtersData));
    toggleFilters();
    getFilteredContacts(filtersData);
  }, [filtersData]);

  useEffect(() => {
    setFiltersData(filters);
  }, [filters, filtersDrawer]);

  const handleFilterChange = useCallback((key, value) => {
    setFiltersData((oldFiltersData) => {
      const updatedFilters = { ...oldFiltersData };
      updatedFilters[key] = value;
      return updatedFilters;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    dispatch(resetContactFilters());
  }, []);

  useEffect(() => {
    if (filtersDrawer) {
      dispatch(getTags());
    }
  }, [filtersDrawer]);

  return (
    <FiltersDrawer
      open={filtersDrawer}
      handleToggleDrawer={toggleFilters}
      handleShowResults={updateFilters}
      filtersCount={newFiltersCount}
      handleResetFilters={handleResetFilters}
    >
      <div className={styles.filtersCont}>
        <CustomField
          label={t('contactFilters.referralsSent')}
          type="number"
          value={filtersData.referrals_sent}
          onChange={(e) => handleFilterChange('referrals_sent', e.target.value)}
        />
        <CustomMultiSelect
          placeholder={t('placeholders.tags')}
          options={formatTags}
          value={filtersData.tags}
          onChange={(options) => handleFilterChange('tags', options)}
        />
        <CustomMultiSelect
          placeholder={t('placeholders.source')}
          options={translatedContactSources}
          value={filtersData.sources}
          onChange={(options) => handleFilterChange('sources', options)}
        />
        <div className={styles.starredCont}>
          <div className={styles.starredInfoCont}>
            <StarredIcon />
            <p className={styles.text}>{t('contactFilters.showOnlyStarred')}</p>
          </div>
          <CustomSwitch
            checked={filtersData.starred}
            onChange={(isChecked) => handleFilterChange('starred', isChecked)}
          />
        </div>
      </div>
    </FiltersDrawer>
  );
};

export default ContactFilters;
